import WebIMConfig from './WebIMConfig';
import store from '@/store/index';
import VoiceFn from '@/views/Voice/fn';
import { setMsgInfo } from '@/views/Voice/store/module/msgInfo';
import { setMsgConfig } from '@/views/Voice/store/module/msgConfig';
import { setRepeatInfo } from '@/views/Voice/store/module/repeatInfo';
import { message } from 'antd';
import { getShowUserInfo } from '../config';
import { transformValuesToString } from '@/views/NewLive/studio/liveChat/hxChatRoom/config';

function getWebIMConn() {
  return (window as any).WebIM?.conn;
}

export function isWebIMConnected() {
  const conn = getWebIMConn();
  if (!conn) {
    return false;
  }
  try {
    if (typeof conn.isOpened === 'function') {
      return conn.isOpened();
    }
    return !!(conn.context && (conn.context.userId || conn.context.jid));
  } catch (error) {
    return false;
  }
}

function swallowPromise(result: any) {
  if (result && typeof result.catch === 'function') {
    result.catch(() => undefined);
  }
}

export function safeLeaveChatRoom(roomId?: string | number) {
  const chatRoomId = roomId ? String(roomId) : '';
  if (!chatRoomId || chatRoomId === '0') {
    return;
  }
  const conn = getWebIMConn();
  if (!conn || !isWebIMConnected()) {
    return;
  }
  try {
    swallowPromise(conn.leaveChatRoom({ roomId: chatRoomId }));
  } catch (error) {
    // 退出房间时忽略 leave 失败
  }
}

export function leaveAllVoiceChatRooms(roomInfo?: any) {
  const info = roomInfo || VoiceFn.getLocalInfo('roomInfo');
  if (!info) {
    return;
  }
  const hxRoomId = Number(info.hxRoomId);
  const mainRoomHxId = Number(info?.mainRoomInfo?.imId);
  if (mainRoomHxId && hxRoomId && hxRoomId !== mainRoomHxId) {
    safeLeaveChatRoom(hxRoomId);
  }
  if (mainRoomHxId) {
    safeLeaveChatRoom(mainRoomHxId);
  }
}

export function initWebIM() {
  const WebIM = (window as any).WebIM;
  if (!WebIM) {
    return;
  }
  WebIM.config = WebIMConfig;
  if (WebIM.conn && isWebIMConnected()) {
    return;
  }
  WebIM.conn = new WebIM.connection({
    appKey: WebIM.config.appkey,
    isMultiLoginSessions: WebIM.config.isMultiLoginSessions,
    https: WebIM.config.https,
    isAutoLogin: true,
    heartBeatWait: WebIM.config.heartBeatWait,
    autoReconnectNumMax: WebIM.config.autoReconnectNumMax,
    autoReconnectInterval: WebIM.config.autoReconnectInterval,
    isStropheLog: WebIM.config.isStropheLog,
    delivery: WebIM.config.delivery,
    isHttpDNS: WebIM.config.isHttpDNS,
    isDebug: false,
    isFixedDeviceId: false
  });
  WebIM.logger.disableAll();
}

export function toLogin(uid: number) {
  (window as any).WebIM.conn.open({
    user: `User${uid}`,
    pwd: `Pwd${uid}`,
    appKey: WebIMConfig.appkey
  });
}

export function toLogout(roomInfo?: any) {
  leaveAllVoiceChatRooms(roomInfo);
  const conn = getWebIMConn();
  if (!conn || !isWebIMConnected()) {
    return;
  }
  try {
    const closeResult = conn.close();
    swallowPromise(closeResult);
    (window as any).WebIM.conn = null;
  } catch (error) {
    (window as any).WebIM.conn = null;
  }
}

export function joinChatRoom(info: any) {
  (window as any).WebIM.conn.joinChatRoom({
    roomId: info.chatroomId,
    success: () => {
      console.log('加入聊天室成功');
    },
    error: () => {
      console.log('加入聊天室失败');
    }
  });
}

export interface errorCodeListJson {
  type: number;
  msg: string;
}

export const errorCodeList: errorCodeListJson[] = [
  {
    type: 219,
    msg: '您已被禁言'
  },
  {
    type: 500,
    msg: '服务器忙碌'
  },
  {
    type: 501,
    msg: '抱歉，包含敏感内容，请修改后再试！'
  },
  {
    type: 502,
    // msg: '消息被拦截'
    msg: '抱歉，包含敏感内容，请修改后再试！'
  },
  {
    type: 503,
    msg: '错误信息超出 SDK 处理范围'
  },
  {
    type: 507,
    msg: '您已被禁言'
  },
  {
    type: 508,
    // msg: '消息被 Moderation 服务拦截'
    msg: '抱歉，包含敏感内容，请修改后再试！'
  },
  {
    type: 509,
    msg: '发送消息超出频率限制'
  },
  {
    type: 602,
    msg: '当前不在聊天室'
  },
  {
    type: 603,
    msg: '被封禁，无法发送消息'
  },
  {
    type: 604,
    msg: '消息回调函数内部错误'
  },
  {
    type: 605,
    msg: '聊天室不存在'
  },
  {
    type: 705,
    msg: '聊天室不存在'
  },
  {
    type: 999,
    msg: 'Websocket 发送消息错误'
  }
];

export function returnErrorMsg(type: number, isObj?: boolean) {
  for (let i = errorCodeList.length - 1; i >= 0; i--) {
    if (errorCodeList[i].type === type) {
      if (isObj) {
        return errorCodeList[i];
      }
      return errorCodeList[i].msg;
    }
  }
  return 'send msg error';
}

export function sendRoomText(
  msgContent: string,
  uid: number,
  roomId: any,
  nickname: any,
  chatroomId: string,
  isHomeowner: boolean,
  sendImg: any,
  authInfo: any,
  roleId: number,
  sex: number
) {
  const WebIM = (window as any).WebIM;
  const msgConfig = VoiceFn.getLocalInfo('msgConfig');
  let id = WebIM.conn.getUniqueId();
  let msg = new WebIM.message('txt', id);
  let option = {
    msg: msgContent,
    to: chatroomId,
    chatType: 'chatRoom',
    ext: {
      uid,
      roomId: roomId.toString(),
      nickname,
      isHomeowner,
      live_no: roomId,
      head_img: sendImg,
      auth_icon: authInfo,
      authIcon: authInfo && authInfo.flagTheme ? authInfo.flagTheme.simpleIconUrl : '',
      roleId,
      sex
    },
    success: () => {
      store.dispatch(
        setMsgConfig({
          ...msgConfig,
          msg: '',
          state: 2,
          errorMsg: ''
        })
      );
      store.dispatch(
        setMsgInfo({
          msgItem: {
            msgTypeId: 1,
            msgType: 'TEXT',
            userId: uid,
            isHomeowner,
            userName: nickname,
            authIcon: authInfo && authInfo.flagTheme ? authInfo.flagTheme.simpleIconUrl : '',
            headImg: sendImg,
            content: msgContent,
            roomId,
            noHistroy: true,
            time: Date.now(),
            roleId,
            sex
          }
        })
      );
    },
    fail: (error: any) => {
      console.log('发送文本信息失败', error);
      const infoObj: any = returnErrorMsg(error.type, true);
      if (infoObj.type === 602) {
        const repeatInfo = VoiceFn.getLocalInfo('repeatInfo');
        store.dispatch(
          setRepeatInfo({
            ...repeatInfo,
            reloadType: 'REPEATIMLOGIN',
            reloadTitle: msgContent,
            reloadTime: Date.now()
          })
        );
        message.info('当前不在聊天室，正在重新连接，请稍等');
      } else {
        store.dispatch(
          setMsgConfig({
            ...msgConfig,
            state: 3,
            errorMsg: returnErrorMsg(error.type)
          })
        );
      }
    }
  };
  msg.set(option);
  WebIM.conn.send(msg.body);
}

export function sendFollowStudio(chatroomId: any, uid: any, nickname: any, liveNo: any, authInfo: any) {
  const WebIM = (window as any).WebIM;
  console.log('发送关注主播');
  const id = WebIM.conn.getUniqueId();
  let msg = new WebIM.message('custom', id);
  const customEvent = 'do_follow';
  const roomInfo = VoiceFn.getLocalInfo('roomInfo');
  const customExts = {
    uid: uid,
    nickname: nickname,
    live_no: liveNo,
    auth_icon: authInfo,
    onAppId: '10001',
    ...getShowUserInfo(roomInfo?.user_96show)
  };
  msg.set({
    to: chatroomId,
    customEvent,
    customExts: transformValuesToString(customExts),
    ext: transformValuesToString(customExts),
    chatType: 'chatRoom',
    success: () => {
      console.log('发送关注主播成功');
    },
    fail: () => {
      console.log('发送关注主播失败');
    }
  });
  WebIM.conn.send(msg.body);
}

export async function sendDelRoomUser(uid: any, roomId: any) {
  const roomInfo = VoiceFn.getLocalInfo('roomInfo');
  const mainRoomImId = roomInfo.mainRoomInfo.imId;
  const WebIM = (window as any).WebIM;
  console.log('发送信息-切换房间');
  const id = WebIM.conn.getUniqueId();
  let msg = new WebIM.message('custom', id);
  const customEvent = 'switch_child_room';
  const customExts = {
    uid,
    roomId,
    onAppId: '10001',
    ...getShowUserInfo(roomInfo?.user_96show)
  };
  msg.set({
    to: mainRoomImId,
    customEvent,
    customExts,
    ext: customExts,
    chatType: 'chatRoom',
    success: () => {
      console.log('发送信息-切换房间-成功');
    },
    fail: (error: any) => {
      console.log('发送信息-切换房间-失败', error);
    }
  });
  WebIM.conn.send(msg.body);
}

export async function sendAddRoomUser(uid: any, roomId: any) {
  const roomInfo = VoiceFn.getLocalInfo('roomInfo');
  const mainRoomImId = roomInfo.mainRoomInfo.imId;
  const WebIM = (window as any).WebIM;
  console.log('发送信息-加入房间');
  const id = WebIM.conn.getUniqueId();
  let msg = new WebIM.message('custom', id);
  const customEvent = 'join_child_room';
  const customExts = {
    uid,
    roomId
  };
  msg.set({
    to: mainRoomImId,
    customEvent,
    customExts,
    ext: customExts,
    chatType: 'chatRoom',
    success: () => {
      console.log('发送信息-加入房间-成功');
    },
    fail: (error: any) => {
      console.log('发送信息-加入房间-失败', error);
    }
  });
  WebIM.conn.send(msg.body);
}

interface FollowAnchorParams {
  chatroomId: string;
  uid: number;
  nickname: string;
  roomId: number;
  roleId: number;
  svipEffect?: any;
  sex?: any;
}

export function toFollowAnchor({ chatroomId, uid, nickname, roomId, roleId, svipEffect, sex }: FollowAnchorParams) {
  const WebIM = (window as any).WebIM;
  console.log('发送关注主播消息');
  const id = WebIM.conn.getUniqueId();
  let msg = new WebIM.message('custom', id);
  const customEvent = 'follow_anchor';
  const roomInfo = VoiceFn.getLocalInfo('roomInfo');
  const customExts = {
    uid,
    nickname,
    roomId,
    roleId,
    sex: sex || 0,
    svip_effect: svipEffect || '',
    onAppId: '10001',
    ...getShowUserInfo(roomInfo?.user_96show)
  };
  msg.set({
    to: chatroomId,
    customEvent,
    customExts: transformValuesToString(customExts),
    ext: transformValuesToString(customExts),
    chatType: 'chatRoom',
    success: () => {
      console.log('发送关注主播消息成功');
      store.dispatch(
        setMsgInfo({
          msgItem: {
            msgType: 'TIP',
            msgTypeId: 18,
            mid: Date.now(),
            isSdk: false,
            userId: uid,
            nickName: nickname,
            time: Date.now(),
            roomId,
            noHistroy: Date.now(),
            roleId,
            sex: sex || 0,
            svip_effect: svipEffect || '',
            onAppId: '10001',
            ...getShowUserInfo(roomInfo?.user_96show)
          }
        })
      );
    },
    fail: (error: any) => {
      console.log('发送关注主播消息失败', error);
    }
  });
  WebIM.conn.send(msg.body);
}

export function cancelFollowAnchor({ chatroomId, uid, nickname, roomId, roleId, svipEffect }: FollowAnchorParams) {
  const WebIM = (window as any).WebIM;
  console.log('发送取消关注主播消息');
  const id = WebIM.conn.getUniqueId();
  let msg = new WebIM.message('custom', id);
  const customEvent = 'cancel_follow_anchor';
  const roomInfo = VoiceFn.getLocalInfo('roomInfo');
  const customExts = {
    uid,
    nickname,
    roomId,
    roleId,
    svip_effect: svipEffect || '',
    onAppId: '10001',
    ...getShowUserInfo(roomInfo?.user_96show)
  };
  msg.set({
    to: chatroomId,
    customEvent,
    customExts: transformValuesToString(customExts),
    ext: transformValuesToString(customExts),
    chatType: 'chatRoom',
    success: () => {
      console.log('发送取消关注主播消息成功');
    },
    fail: (error: any) => {
      console.log('发送取消关注主播消息失败', error);
    }
  });
  WebIM.conn.send(msg.body);
}

export async function wakeUpChatRoom() {
  try {
    const roomInfo = VoiceFn.getLocalInfo('roomInfo');
    const mainRoomImId = roomInfo.mainRoomInfo.imId;
    const WebIM = (window as any).WebIM;
    const id = WebIM.conn.getUniqueId();
    let msg = new WebIM.message('custom', id);
    msg.set({
      to: mainRoomImId,
      customEvent: 'WAKE_UP_CHAT_ROOM',
      chatType: 'chatRoom',
      success: () => {
        // console.log('WAKE_UP_CHAT_ROOM_SUCCESS');
      },
      fail: (error: any) => {
        // console.log('WAKE_UP_CHAT_ROOM_FAIL');
      }
    });
    WebIM.conn.send(msg.body);
  } catch (error) {
    console.log(error);
  }
}
